import { useState, useRef } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { ASSETS } from "../i18n";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.3em] text-tobacco">
    <span className="h-px w-8 bg-gold" />
    {children}
  </span>
);

/* ----------------------------- HERO ----------------------------- */
export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-screen min-h-[640px] overflow-hidden bg-ink">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={ASSETS.hero} alt="Raw gold in mineral quartz" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/30 to-ink/80" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative h-full flex flex-col items-center justify-center text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={ASSETS.sealLight} alt="TOTUMO seal" className="h-20 md:h-24 w-auto mx-auto mb-10 opacity-95" />
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-[0.35em] text-ivory pl-[0.35em]">
            TOTUMO
          </h1>
          <p className="mt-5 font-sans text-xs sm:text-sm uppercase tracking-[0.45em] text-ivory/80 pl-[0.45em]">
            {t("hero.subtitle")}
          </p>
          <div className="mx-auto my-9 h-px w-16 bg-gold" />
          <p className="font-serif italic text-2xl md:text-3xl text-ivory/90">{t("hero.tagline")}</p>
          <p className="mt-3 font-sans text-sm tracking-[0.15em] text-ivory/60">{t("hero.line")}</p>

          <a
            data-testid="hero-inquiries-cta"
            href="#inquiries"
            onClick={(e) => { e.preventDefault(); document.getElementById("inquiries")?.scrollIntoView({ behavior: "smooth" }); }}
            className="inline-block mt-12 font-sans text-[0.7rem] uppercase tracking-[0.25em] border border-ivory/60 text-ivory px-9 py-4 hover:bg-ivory hover:text-ink transition-all duration-500"
          >
            {t("cta.inquiries")}
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="font-sans text-[0.6rem] uppercase tracking-[0.3em] text-ivory/50">{t("hero.scroll")}</span>
        <span className="h-10 w-px bg-ivory/40 animate-pulse" />
      </div>
    </section>
  );
};

/* ---------------------------- CREDENTIAL BAR ---------------------------- */
export const CredentialBar = () => {
  const { t } = useLang();
  return (
    <div data-testid="credential-bar" className="bg-ink text-ivory">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:gap-x-10">
        {t("strip").map((s, i) => (
          <span key={i} className="flex items-center gap-6 md:gap-10">
            <span className="font-sans text-[0.6rem] md:text-[0.68rem] uppercase tracking-[0.24em] text-ivory/70">{s}</span>
            {i < t("strip").length - 1 && <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-gold" />}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------------------- THE HOUSE ---------------------------- */
export const House = () => {
  const { t } = useLang();
  return (
    <section id="house" data-testid="house-section" className="bg-white py-24 md:py-32 lg:py-44">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>{t("house.eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-ink">
                {t("house.title")}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6">
            {t("house.body").map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="font-serif text-lg md:text-xl leading-relaxed text-ink/75">{p}</p>
              </Reveal>
            ))}
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 pt-8 border-t border-ink/10 mt-10">
              {t("house.pillars").map((p, i) => (
                <Reveal key={p.k} delay={0.15 + i * 0.06}>
                  <p className="font-serif text-xl text-ink">{p.k}</p>
                  <p className="mt-2 font-serif text-base leading-relaxed text-ink/65">{p.v}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- ORIGIN ---------------------------- */
export const Origin = () => {
  const { t } = useLang();
  return (
    <section id="origin" data-testid="origin-section" className="bg-ink text-ivory">
      <div className="grid lg:grid-cols-2">
        <div className="relative min-h-[55vh] lg:min-h-[88vh] overflow-hidden">
          <img src={ASSETS.territory} alt="Origin territory" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/20" />
        </div>
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-0 flex items-center">
          <div className="max-w-xl">
            <Reveal><Eyebrow>{t("origin.eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15]">
                {t("origin.title")}
              </h2>
            </Reveal>
            {t("origin.body").map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ivory/80">{p}</p>
              </Reveal>
            ))}
            <div className="mt-12 space-y-px">
              {t("origin.meta").map((m, i) => (
                <Reveal key={m.k} delay={0.15 + i * 0.06}>
                  <div className="flex items-baseline justify-between gap-6 py-4 border-t border-ivory/15">
                    <span className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-ivory/50">{m.k}</span>
                    <span className="font-serif text-lg text-ivory text-right">{m.v}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- OPERATIONS ---------------------------- */
export const Operations = () => {
  const { t } = useLang();
  return (
    <section id="operations" data-testid="operations-section" className="bg-ivory py-24 md:py-32 lg:py-44">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>{t("operations.eyebrow")}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-ink">
              {t("operations.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-serif text-xl leading-relaxed text-ink/75">{t("operations.intro")}</p>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 mt-16 border border-ink/10">
          {t("operations.steps").map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="group bg-ivory p-8 md:p-10 h-full hover:bg-[#171713] transition-colors duration-700">
                <span className="font-serif text-4xl text-gold/70 group-hover:text-gold transition-colors duration-700">{s.n}</span>
                <h3 className="mt-6 font-serif text-2xl text-ink group-hover:text-ivory transition-colors duration-700">{s.k}</h3>
                <p className="mt-3 font-serif text-base leading-relaxed text-ink/65 group-hover:text-ivory/65 transition-colors duration-700">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- STANDARDS ---------------------------- */
export const Standards = () => {
  const { t } = useLang();
  return (
    <section id="standards" data-testid="standards-section" className="bg-white py-24 md:py-32 lg:py-44">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative min-h-[50vh] lg:min-h-[70vh] overflow-hidden">
            <img src={ASSETS.assay} alt="Assay and weighing" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><Eyebrow>{t("standards.eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-ink">
                {t("standards.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75">{t("standards.intro")}</p>
            </Reveal>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-10">
              {t("standards.items").map((it, i) => (
                <Reveal key={it.k} delay={0.12 + i * 0.05}>
                  <div className="py-5 border-t border-ink/10">
                    <p className="font-serif text-xl text-ink">{it.k}</p>
                    <p className="mt-1.5 font-serif text-base leading-relaxed text-ink/65">{it.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- RESPONSIBILITY ---------------------------- */
export const Responsibility = () => {
  const { t } = useLang();
  return (
    <section id="responsibility" data-testid="responsibility-section" className="relative bg-[#171713] text-ivory overflow-hidden">
      <img src={ASSETS.earth} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-[#171713]/85" />
      <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-44">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal><span className="inline-flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.3em] text-ivory/70"><span className="h-px w-8 bg-gold" />{t("responsibility.eyebrow")}</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15]">
                {t("responsibility.title")}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            {t("responsibility.body").map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="font-serif text-xl leading-relaxed text-ivory/85 mb-6">{p}</p>
              </Reveal>
            ))}
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-7 mt-8">
              {t("responsibility.points").map((p, i) => (
                <Reveal key={p.k} delay={0.15 + i * 0.06}>
                  <p className="font-serif text-xl text-ivory">{p.k}</p>
                  <p className="mt-2 font-serif text-base leading-relaxed text-ivory/70">{p.v}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- GLOBAL TRADE ---------------------------- */
export const GlobalTrade = () => {
  const { t } = useLang();
  return (
    <section id="trade" data-testid="trade-section" className="bg-white text-ink">
      <div className="grid lg:grid-cols-2">
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-0 flex items-center order-2 lg:order-1">
          <div className="max-w-xl">
            <Reveal><span className="inline-flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.3em] text-tobacco"><span className="h-px w-8 bg-gold" />{t("trade.eyebrow")}</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-ink">{t("trade.title")}</h2>
            </Reveal>
            {t("trade.body").map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75">{p}</p>
              </Reveal>
            ))}
            <div className="mt-12 grid grid-cols-3 gap-6">
              {t("trade.stats").map((s, i) => (
                <Reveal key={s.k} delay={0.15 + i * 0.06}>
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-ink/40">{s.k}</p>
                  <p className="mt-2 font-serif text-base text-ink leading-snug">{s.v}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
        <div className="relative min-h-[55vh] lg:min-h-[88vh] overflow-hidden order-1 lg:order-2">
          <img src={ASSETS.trade} alt="Global trade and export" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
};

/* ---------------------------- INQUIRIES ---------------------------- */
const initialForm = { name: "", email: "", company: "", subject: "", message: "" };

export const Inquiries = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t("inquiries.form.success"));
      setForm(initialForm);
    } catch (err) {
      toast.error(t("inquiries.form.error"));
    } finally {
      setLoading(false);
    }
  };

  const f = t("inquiries.form");
  const inputCls =
    "w-full bg-transparent border-b border-ink/25 py-3 font-sans text-base text-ink placeholder-ink/40 focus:border-gold focus:outline-none transition-colors duration-500";

  return (
    <section id="inquiries" data-testid="inquiries-section" className="bg-white py-24 md:py-32 lg:py-44">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>{t("inquiries.eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-ink">
                {t("inquiries.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75 max-w-md">{t("inquiries.body")}</p>
            </Reveal>
            <div className="mt-12 space-y-6">
              {[
                { k: t("inquiries.office"), v: t("inquiries.officeValue") },
                { k: t("inquiries.phoneLabel"), v: t("inquiries.phoneValue") },
              ].map((m) => (
                <Reveal key={m.k}>
                  <div className="border-t border-ink/10 pt-4">
                    <p className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-tobacco">{m.k}</p>
                    <p className="mt-1 font-serif text-xl text-ink">{m.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form data-testid="inquiry-form" onSubmit={onSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input data-testid="inquiry-name" name="name" required value={form.name} onChange={onChange} placeholder={f.name} className={inputCls} />
                  <input data-testid="inquiry-email" type="email" name="email" required value={form.email} onChange={onChange} placeholder={f.email} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <input data-testid="inquiry-company" name="company" value={form.company} onChange={onChange} placeholder={f.company} className={inputCls} />
                  <input data-testid="inquiry-subject" name="subject" value={form.subject} onChange={onChange} placeholder={f.subject} className={inputCls} />
                </div>
                <textarea data-testid="inquiry-message" name="message" required rows={5} value={form.message} onChange={onChange} placeholder={f.message} className={`${inputCls} resize-none`} />
                <button
                  data-testid="inquiry-submit"
                  type="submit"
                  disabled={loading}
                  className="font-sans text-[0.7rem] uppercase tracking-[0.25em] border border-ink px-10 py-4 text-ink hover:bg-ink hover:text-ivory transition-all duration-500 disabled:opacity-50"
                >
                  {loading ? f.sending : f.submit}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
